import React, { useEffect, useRef, useState, type ReactNode } from 'react';

import { Text } from '@/nova/components';
import type { ShowcaseVideo } from '@/nova/mock-data/types';
import { getClip } from './videoCache';
import { FallbackLayer, Frame, Slot } from './VideoStage.styles';

export type VideoStageProps = {
  video?: ShowcaseVideo;
  fallback?: ReactNode;
};

/**
 * Centre demo clip for the active showcase highlight. The clip plays inside a
 * fixed black box (so swapping sources never reflows the layout). Elements are
 * pulled from a persistent per-source cache ({@link getClip}): the clip streams
 * high-res from the first frame (HLS locked to the top rendition) and returning to
 * a clip replays from the buffered, cached element with no new network requests.
 */
export const VideoStage = ({ video, fallback }: VideoStageProps): React.ReactElement => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;

    if (!video?.src) {
      box.replaceChildren();
      setFailed(true);
      return undefined;
    }

    const clip = getClip(video.src, video.loop ?? true);
    setFailed(clip.failed);
    const unsubscribe = clip.onFail(() => setFailed(true));

    // Move the cached element here (out of any previous container) and (re)start it.
    box.replaceChildren(clip.video);
    try {
      clip.video.currentTime = 0;
    } catch {
      /* not seekable yet — it'll start from 0 anyway */
    }
    clip.video.play().catch(() => {});

    return () => {
      unsubscribe();
      clip.video.pause();
      // Detach so the next source's element can mount; the element stays cached + buffered.
      if (clip.video.parentElement === box) {
        box.removeChild(clip.video);
      }
    };
  }, [video?.src, video?.loop]);

  return (
    <Frame>
      <Slot ref={boxRef} />
      {failed && <FallbackLayer>{fallback ?? <Text text="Preview unavailable" />}</FallbackLayer>}
    </Frame>
  );
};

export default VideoStage;
